import type { WorkerRole } from '../../core/index.js'
import type { SchedulingRules, ShiftTypeKey } from '../../facilities/index.js'
import type { AvailableShift, PublishShiftInput } from '../types.js'
import { shiftMonth } from './scheduleCalendarData.js'

/** What the Publish shifts form asks: a run of dates, the shift types and units to open on each, the seats and the role. */
export interface PublishShiftsForm {
  from: string
  to: string
  shiftTypes: readonly ShiftTypeKey[]
  units: readonly string[]
  capacity: number
  role: WorkerRole | null
}

/** The longest run of dates one publish takes: three months, so a slip of the year does not open a year of shifts. */
export const MAX_PUBLISH_DAYS = 92
export const MAX_CAPACITY = 50
/** Seats per request: the API writes each seat behind the gateway's 30 seconds, so a large publish goes up in parts. */
export const SEATS_PER_REQUEST = 200

const DAY_MS = 86_400_000

/** Every date from `from` to `to` inclusive, `YYYY-MM-DD`. */
export function datesBetween(from: string, to: string): string[] {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return []
  }
  return Array.from({ length: Math.round((end - start) / DAY_MS) + 1 }, (_, index) => new Date(start + index * DAY_MS).toISOString().slice(0, 10))
}

function lastDayOf(month: string): string {
  return new Date(Date.parse(`${shiftMonth(month, 1)}-01T00:00:00Z`) - DAY_MS).toISOString().slice(0, 10)
}

/**
 * The units the form starts with: where the facility keeps a unit list, every
 * unit on it; else those already published, else one called `main`.
 */
export function defaultUnits(rules: Pick<SchedulingRules, 'units'>, existing: readonly AvailableShift[]): string[] {
  if (rules.units.length > 0) {
    return rules.units.map((unit) => unit.id)
  }
  const published = [...new Set(existing.map((shift) => shift.unitId))].sort()
  if (published.length > 0) {
    return published
  }
  return ['main']
}

/**
 * The month the form starts on: the first from next month on with nothing
 * published, looking four months ahead - the gap a facility most likely
 * came to fill - else next month. Its first day is never before today.
 */
export function defaultPublishForm(rules: Pick<SchedulingRules, 'units' | 'shiftTypes'>, existing: readonly AvailableShift[], today: string): PublishShiftsForm {
  const thisMonth = today.slice(0, 7)
  const months = [1, 2, 3, 4].map((offset) => shiftMonth(thisMonth, offset))
  const month = months.find((candidate) => !existing.some((shift) => shift.date.startsWith(`${candidate}-`))) ?? months[0]!
  const first = `${month}-01`
  return {
    from: first < today ? today : first,
    to: lastDayOf(month),
    shiftTypes: rules.shiftTypes.filter((entry) => entry.enabled).map((entry) => entry.key),
    units: defaultUnits(rules, existing),
    capacity: 1,
    role: null,
  }
}

/** "unit-a, unit-b" typed in a box, as a list of unit ids. */
export function parseUnits(text: string): string[] {
  return [...new Set(text.split(',').map((unit) => unit.trim()).filter((unit) => unit !== ''))]
}

/** Why the form cannot be published as it stands, in the reader's words; null when it can. */
export function publishBlocker(form: PublishShiftsForm, today: string): string | null {
  const days = datesBetween(form.from, form.to).length
  if (!form.from || !form.to || days === 0) {
    return 'Choose a first and last date, the last on or after the first.'
  }
  if (form.from < today) {
    return 'Shifts can be published from today on.'
  }
  if (days > MAX_PUBLISH_DAYS) {
    return `Publish at most ${MAX_PUBLISH_DAYS} days at a time.`
  }
  if (form.shiftTypes.length === 0) {
    return 'Choose at least one shift type.'
  }
  if (form.units.length === 0) {
    return 'Choose at least one unit.'
  }
  if (!Number.isInteger(form.capacity) || form.capacity < 1 || form.capacity > MAX_CAPACITY) {
    return `Seats per shift is a whole number from 1 to ${MAX_CAPACITY}.`
  }
  return null
}

/** Every shift the form opens: each date, each unit, each shift type at the catalogue's hours. */
export function publishInputsOf(form: PublishShiftsForm, rules: Pick<SchedulingRules, 'shiftTypes'>): PublishShiftInput[] {
  const catalogue = rules.shiftTypes.filter((entry) => form.shiftTypes.includes(entry.key))
  return datesBetween(form.from, form.to).flatMap((date) =>
    form.units.flatMap((unitId) =>
      catalogue.map((entry) => ({
        date,
        period: entry.key,
        startTime: entry.start,
        endTime: entry.end,
        unitId,
        ...(form.role ? { role: form.role } : {}),
        ...(form.capacity > 1 ? { capacity: form.capacity } : {}),
      })),
    ),
  )
}

/** The inputs in parts of at most `SEATS_PER_REQUEST` seats, each part one request. */
export function publishBatches(inputs: readonly PublishShiftInput[]): PublishShiftInput[][] {
  const perRequest = Math.max(1, Math.floor(SEATS_PER_REQUEST / Math.max(1, inputs[0]?.capacity ?? 1)))
  return Array.from({ length: Math.ceil(inputs.length / perRequest) }, (_, index) => inputs.slice(index * perRequest, (index + 1) * perRequest))
}

/** The shifts as the admin's shifts import takes them: CSV with the columns date, unitId, period, startTime, endTime, role, capacity. */
export function publishCsvOf(inputs: readonly PublishShiftInput[]): string {
  const rows = inputs.map((input) => [input.date, input.unitId, input.period, input.startTime, input.endTime, input.role ?? '', String(input.capacity ?? 1)].join(','))
  return ['date,unitId,period,startTime,endTime,role,capacity', ...rows].join('\n') + '\n'
}

/** How many published seats already fall in the form's dates. */
export function publishedInRange(form: Pick<PublishShiftsForm, 'from' | 'to'>, existing: readonly AvailableShift[]): number {
  return existing.filter((shift) => shift.date >= form.from && shift.date <= form.to).length
}

/** What publishing a finding's missing seats needs: the unit, shift type and role it names, and how many on each date. */
export interface MissingSeats {
  unitId: string
  shiftType: ShiftTypeKey
  role: WorkerRole | null
  seatsByDate: Readonly<Record<string, number>>
}

/**
 * The shifts that publish exactly the seats a Month card finding says were
 * never published: on each date the shift for the unit, shift type and role at
 * the catalogue's hours, its capacity the seats already published under that
 * shift plus the ones missing - publishing a shift again keeps its existing
 * seats (and any a worker holds) and adds the rest. Dates before today, and a
 * shift type the facility does not offer, are left out.
 */
export function missingSeatInputs(missing: MissingSeats, rules: Pick<SchedulingRules, 'shiftTypes'>, existing: readonly AvailableShift[], today: string): PublishShiftInput[] {
  const type = rules.shiftTypes.find((entry) => entry.key === missing.shiftType && entry.enabled)
  if (!type) {
    return []
  }
  return Object.entries(missing.seatsByDate)
    .filter(([date, seats]) => date >= today && seats > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, seats]) => {
      const already = existing.filter((shift) => shift.date === date && shift.unitId === missing.unitId && shift.period === missing.shiftType && (shift.role ?? null) === missing.role).length
      const capacity = Math.min(MAX_CAPACITY, already + seats)
      return { date, period: missing.shiftType, startTime: type.start, endTime: type.end, unitId: missing.unitId, ...(missing.role ? { role: missing.role } : {}), ...(capacity > 1 ? { capacity } : {}) }
    })
}
